import creditCardType from 'credit-card-type';
import { sanitizeNumber } from './format';

export const formatMonetary = (value: string, hasSufix = true) => {
  if (!value || value === 'R$ 0,0') return '';

  value = value.replace(/\D/g, '');

  while (value.length < 3) {
    value = '0' + value;
  }

  const cents = value.slice(-2);
  const reais = value.slice(0, -2).replace(/^0+/, '');

  const reaisNumber = Number(reais.length ? reais : '0');
  const reaisFormatted = reaisNumber.toLocaleString('pt-BR');

  return `${hasSufix ? 'R$ ' : ''}${reaisFormatted},${cents}`;
};

export const formatCPF = (cpfNumber: string) => {
  let cleaned = sanitizeNumber(cpfNumber);

  if (cleaned.length > 9) {
    cleaned = cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  } else if (cleaned.length > 6) {
    cleaned = cleaned.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else if (cleaned.length > 3) {
    cleaned = cleaned.replace(/(\d{3})(\d{0,3})/, '$1.$2');
  } else {
    cleaned = cleaned.replace(/^(\d*)/, '$1');
  }

  return cleaned.substring(0, 14);
};

export const formatCNPJ = (cpfNumber: string) => {
  let cleaned = sanitizeNumber(cpfNumber);

  if (cleaned.length > 12) {
    cleaned = cleaned.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/,
      '$1.$2.$3/$4-$5'
    );
  } else if (cleaned.length > 8) {
    cleaned = cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
  } else if (cleaned.length > 5) {
    cleaned = cleaned.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else if (cleaned.length > 2) {
    cleaned = cleaned.replace(/(\d{2})(\d{0,3})/, '$1.$2');
  } else {
    cleaned = cleaned.replace(/^(\d*)/, '$1');
  }

  return cleaned.substring(0, 18);
};

export const formatPhoneNumber = (phoneNumber: string) => {
  let cleaned = sanitizeNumber(phoneNumber.replace('+55', ''));

  if (cleaned.length > 10) {
    cleaned = cleaned.replace(/^(\d\d)(\d{5})(\d{0,4}).*/, '($1) $2-$3');
  } else if (cleaned.length > 6) {
    cleaned = cleaned.replace(/^(\d\d)(\d{4})(\d{0,4}).*/, '($1) $2-$3');
  } else if (cleaned.length > 2) {
    cleaned = cleaned.replace(/^(\d\d)(\d{0,5})/, '($1) $2');
  } else {
    cleaned = cleaned.replace(/^(\d*)/, '$1');
  }

  return cleaned.substring(0, 15);
};

export const formatCreditCard = (creditCardNumber: string): string => {
  const cleaned = sanitizeNumber(creditCardNumber);

  if (cleaned.length <= 8) {
    return formatNotIdentifiedCard(cleaned);
  }

  const cards = creditCardType(cleaned);

  if (cards.length > 0) {
    const cardType = cards[0].type as keyof typeof formatCardByType;
    const formatCardNumber =
      formatCardByType[cardType] || formatCardByType.default;
    return formatCardNumber(cleaned);
  }

  return formatGeneralCard(cleaned);
};

const formatNotIdentifiedCard = (cleanedValue: string) => {
  if (cleanedValue.length <= 4) {
    return cleanedValue;
  }
  return cleanedValue.replace(/(\d{4})(\d{0,4})/, '$1 $2').substring(0, 9);
};

const formatGeneralCard = (cleanedValue: string) => {
  if (cleanedValue.length > 12) {
    return cleanedValue
      .replace(/(\d{4})(\d{4})(\d{4})(\d{0,4})/, '$1 $2 $3 $4')
      .substring(0, 19);
  }
  return cleanedValue
    .replace(/(\d{4})(\d{4})(\d{0,4})/, '$1 $2 $3')
    .substring(0, 19);
};

const formatAmericanExpressCard = (cleanedValue: string) => {
  if (cleanedValue.length > 10) {
    return cleanedValue
      .replace(/(\d{4})(\d{6})(\d{0,5})/, '$1 $2 $3')
      .substring(0, 17);
  }
  return cleanedValue.replace(/(\d{4})(\d{0,6})/, '$1 $2').substring(0, 17);
};

const formatDinersClubCard = (cleanedValue: string) => {
  if (cleanedValue.length > 9) {
    return cleanedValue
      .replace(/(\d{5})(\d{4})(\d{0,5})/, '$1 $2 $3')
      .substring(0, 16);
  }
  return cleanedValue.replace(/(\d{5})(\d{4})/, '$1 $2').substring(0, 16);
};

const formatCardByType = {
  'american-express': formatAmericanExpressCard,
  'diners-club': formatDinersClubCard,
  default: formatGeneralCard,
};

export const formatCreditCardValidity = (validity: string) => {
  const cleaned = sanitizeNumber(validity);
  const month = cleaned.substring(0, 2);
  const year = cleaned.substring(2, 6);

  if (cleaned.length > 2) {
    return `${month}/${year}`;
  }

  return cleaned;
};

export const formatStringTime = (time: string) => {
  const cleaned = sanitizeNumber(time);

  if (cleaned.length > 2) {
    return cleaned
      .replace(/^([01]\d|2[0-3])([0-5]\d)$/, '$1:$2')
      .substring(0, 5);
  }

  return cleaned;
};

export const formatStringDate = (date: string) => {
  const cleaned = sanitizeNumber(date);

  if (cleaned.length > 4) {
    return cleaned
      .replace(/^(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3')
      .substring(0, 10);
  } else if (cleaned.length > 2) {
    return cleaned.replace(/^(\d{2})(\d{0,2})/, '$1/$2');
  }

  return cleaned;
};

export const formatRangeAge = (age: string): string => {
  const cleaned = sanitizeNumber(age);
  const num = parseInt(cleaned);

  if (isNaN(num)) return '';
  if (num > 120) return '120';
  if (num < 0) return '0';

  return num.toString();
};

export const formatAgeTwoDigits = (age: string): string => {
  const cleaned = sanitizeNumber(age);
  return cleaned.replace(/^(\d{0,2}).*/, '$1');
};

export const anonymizeEmail = (email: string) => {
  const [username, domain] = email.split('@');

  const domainParts = domain.split('.');
  const domainName = domainParts.slice(0, domainParts.length - 1).join('.');
  const domainExtension = domainParts[domainParts.length - 1];

  const usernameAnonymized =
    username.slice(0, 2) + '*'.repeat(username.length - 2);
  const domainAnonymized =
    domainName.slice(0, 2) + '*'.repeat(domainName.length - 2);

  return `${usernameAnonymized}@${domainAnonymized}.${domainExtension}`;
};

export const anonymizePhone = (phone: string) =>
  phone.slice(0, 2) + '*'.repeat(phone.length - 4) + phone.slice(-2);

export const anonymizeFirstName = (name: string) =>
  name.split(' ')[0].replace(/(?<=.)./g, '*');
